import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { ControlButton } from '@/pages/CADPage/components/ControlPanel/ControlButton'
import { StepsToggledOffIcon } from '@/components/icons/StepsToggledOffIcon'
import { StepsToggledOnIcon } from '@/components/icons/StepsToggledOnIcon'
import { useCADPageStore } from '@/state'
import { useRefreshCADVisibility } from '@/pages/CADPage/components/ControlPanel/hooks/useRefreshCADVisibility'

/**
 * Controls the visibility of all operations that occur
 * before the current operation in the sequence.
 */

interface ToggleStepsButtonProps {
  disabled?: boolean
}

export const ToggleStepsButton = ({ disabled }: ToggleStepsButtonProps) => {
  const { refreshCADVisibility, isLoading } = useRefreshCADVisibility()

  const setCADPageState = useCADPageStore(useShallow((state) => state.setState))

  const isToggledOn = useCADPageStore((state) => state.hidePreviousOperations)

  const Icon = useMemo(() => {
    return isToggledOn ? StepsToggledOnIcon : StepsToggledOffIcon
  }, [isToggledOn])

  const title = useMemo(() => {
    return isToggledOn ? 'Show previous steps' : 'Hide previous steps'
  }, [isToggledOn])

  const onToggle = useCallback(() => {
    setCADPageState({
      hidePreviousOperations: !isToggledOn,
    })

    refreshCADVisibility({
      hidePreviousOperations: !isToggledOn,
    })
  }, [isToggledOn, refreshCADVisibility, setCADPageState])

  return (
    <ControlButton
      title={title}
      icon={
        <Icon className="w-6 h-6" color={isToggledOn ? '#3054EF' : undefined} />
      }
      onClick={onToggle}
      disabled={disabled || isLoading}
    />
  )
}
